import { useEffect, useRef, useState, type ReactNode } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { AccountService, type AccountRead } from '../../client'
import { hasStatus } from '../../lib/api'
import { auth } from '../../lib/firebase'
import { signInWithMagicToken } from '../../lib/magicLink'
import posthog from '../../lib/posthog'
import { AuthContext, type AuthState } from './useAuth'

const ACTIVE_BRAND_KEY = 'activeBrand'

function storedBrandId() {
  try {
    return Number(localStorage.getItem(ACTIVE_BRAND_KEY)) || null
  } catch {
    return null
  }
}

function activeBrand(account: AccountRead | null, preferredId: number | null) {
  const brands = account?.brands ?? []
  return (
    brands.find((brand) => brand.id === preferredId && !brand.locked) ??
    brands.find((brand) => !brand.locked) ??
    brands[0] ??
    null
  )
}

async function loadAccount(user: User | null) {
  if (!user) return null

  try {
    return await AccountService.me()
  } catch (error) {
    if (!hasStatus(error, 404)) console.error(error)
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Pick<AuthState, 'user' | 'account' | 'loading'>>({
    user: null,
    account: null,
    loading: true,
  })
  const [preferredBrandId, setPreferredBrandId] = useState(storedBrandId)
  const identifiedUserId = useRef<string | null>(null)

  useEffect(() => {
    const magicSignIn = signInWithMagicToken()

    return onAuthStateChanged(auth, async () => {
      await magicSignIn
      const user = auth.currentUser

      if (user) {
        if (identifiedUserId.current && identifiedUserId.current !== user.uid) posthog.reset()

        posthog.identify(user.uid, {
          ...(user.email ? { email: user.email } : {}),
          ...(user.displayName ? { name: user.displayName } : {}),
        })
        identifiedUserId.current = user.uid
      } else if (identifiedUserId.current) {
        posthog.reset()
        identifiedUserId.current = null
      }

      setState({ user, account: await loadAccount(user), loading: false })
    })
  }, [])

  async function refreshAccount() {
    const user = auth.currentUser
    setState({ user, account: await loadAccount(user), loading: false })
  }

  function selectBrand(brandId: number) {
    setPreferredBrandId(brandId)
    try {
      localStorage.setItem(ACTIVE_BRAND_KEY, String(brandId))
    } catch {
      return
    }
  }

  const brand = activeBrand(state.account, preferredBrandId)

  return <AuthContext value={{ ...state, brand, selectBrand, refreshAccount }}>{children}</AuthContext>
}
